import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Dialog, DialogTitle, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TextField, Button, FormControlLabel, Tooltip, styled, tooltipClasses, Stack, Snackbar, Alert, CircularProgress, IconButton } from "@mui/material";
import InputIcon from "@mui/icons-material/Input";
import OutputIcon from "@mui/icons-material/Output";
import CategoryIcon from "@mui/icons-material/Category";
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch, useSelector } from "react-redux";
import SaveRevert from "./SaveRevert";
import SaveWarning from "./SaveWarning";
import SaveDiffViewer from "./SaveDiffViewer";
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { api } from "../../redux/api";
import { treeActions } from "../../redux/slices/treeSlice";
import TableLoading from "./TableLoading";
import { yellow, green, red } from "@mui/material/colors";
import { rcTreeToInboundTemplate, rcTreeToOutboundTemplate, rcTreeToOutboundXML, rcTreeToWorkflowTemplate } from "../../utils/rctreeDataConversion";
import { getSelectedNodes } from "../RightContextMenu/ContextActions/CopyAndPaste";
import LightTooltip from "../../commonComponents/LightTooltip";
import VersionConflictDialog from "../../pages/deploy/ContextMenuActions/VersionConflictDialog";
import { checkMultipleTemplatesVersionConflicts, filterTemplatesAfterConflictResolution } from "../../utils/versionConflictChecker";

export default function SaveConfirmationDialog({ reloadPopup, setReloadPopup, dialogOpen, setDialogOpen, unsavedList, setUnsavedList, listLoading, setListLoading, selectedTemplate, setSelectedTemplate, checkedTemplates, setCheckedTemplates, setAlertOpen }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const treeData = useSelector((state) => state.treeData);
  const { account } = useSelector(state => state.queryParams);
  const [comment, setComment] = useState('');
  const [saved, setSaved] = useState(false);
  const [deployServer, setDeployServer] = useState(false);
  const [whatChangedOpen, setWhatChangedOpen] = useState(false);
  const [revertModalOpen, setRevertModalOpen] = useState(false);
  const [warningsModalOpen, setWarningsModalOpen] = useState(false);
  const [selectedTemplateInfo, setSelectedTemplateInfo] = useState(false);
  
  // Version conflict dialog state
  const [showVersionWarning, setShowVersionWarning] = useState(false);
  const [conflictItems, setConflictItems] = useState([]);
  const [currentConflictIndex, setCurrentConflictIndex] = useState(0);
  const [currentConflictItem, setCurrentConflictItem] = useState(null);
  const [conflictDecisions, setConflictDecisions] = useState([]);
  const [itemsToSkip, setItemsToSkip] = useState([]);
  const [conflictingTemplates, setConflictingTemplates] = useState(null);
  const [nonConflictingTemplates, setNonConflictingTemplates] = useState(null);
  
  const [saveLoading, setSaveLoading] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: '' });
  const [saveResults, setSaveResults] = useState([]);
  
  const handleModalClose = () => {
    // Reset version conflict state when closing dialog
    setShowVersionWarning(false);
    setConflictItems([]);
    setCurrentConflictIndex(0);
    setCurrentConflictItem(null);
    setConflictDecisions([]);
    setItemsToSkip([]);
    setConflictingTemplates(null);
    setNonConflictingTemplates(null);
    setSnackbar({ open: false, message: '', severity: '' });
    setIsDeploying(false);
    setDialogOpen(false);
  };
  const handleRowClick = (path) => setSelectedTemplate(path);
  const handleWhatChangedOpen = () => setWhatChangedOpen(true);
  const handleDisplayWarnings = () => setWarningsModalOpen(true);
  const handleRevertSelectedItems = () => setRevertModalOpen(true);
  const getTemplateInfoByPath = (path) => unsavedList.filter(obj => obj.path === path)[0];
  

  // Function to group templates by save destination (server vs user space)
  const groupTemplatesBySaveDestination = (decisions) => {
    const serverTemplates = { inbound_templates: [], outbound_templates: [], workflow: [] };
    const userSpaceTemplates = { inbound_templates: [], outbound_templates: [], workflow: [] };
    
    // Add non-conflicting templates to server (always save to server)
    if (nonConflictingTemplates) {
      serverTemplates.inbound_templates.push(...(nonConflictingTemplates.inbound_templates || []));
      serverTemplates.outbound_templates.push(...(nonConflictingTemplates.outbound_templates || []));
      serverTemplates.workflow.push(...(nonConflictingTemplates.workflow || []));
    }
    
    // Process conflicting templates based on decisions
    decisions.forEach(({ item, decision }) => {
      const { templateData } = item;
      const template = conflictingTemplates?.[`${templateData.template_type}_templates`]?.find(t => 
        t.template === templateData.template && t.workflow_group === templateData.workflow_group
      ) || conflictingTemplates?.workflow?.find(t => 
        templateData.template_type === 'workflow' && t.template === templateData.template && t.workflow_group === templateData.workflow_group
      );
      
      if (template) {
        const targetKey = templateData.template_type === 'workflow' ? 'workflow' : `${templateData.template_type}_templates`;
        const destination = decision === 'YES' ? serverTemplates : userSpaceTemplates;
        destination[targetKey].push(template);
      }
    });
    
    return { serverTemplates, userSpaceTemplates };
  };

  // Function to check if template group has any templates
  const hasTemplates = (templates) => 
    (templates.inbound_templates?.length || 0) + 
    (templates.outbound_templates?.length || 0) + 
    (templates.workflow?.length || 0) > 0;

  const proceedWithSaveOnly = async (saveData, shouldSaveToServer) => {
    try {
      const res = await dispatch(api.endpoints.saveAllTemplates.initiate({
        comment,
        save_to_server: shouldSaveToServer,
        data: saveData
      }, { forceRefetch: true }));
      
      const saveResults = res.data?.data?.results || [];
      const saveErrors = res.data?.data?.errors || [];
      
      if (saveErrors.length > 0) {
        console.warn('Save Errors:', saveErrors);
      }

      if (res.isSuccess && res.data?.current_state === "SUCCESS") {
        // Only update template saved status, skip all refresh operations
        setTemplatesSaved(saveResults, true); 
        
        return { success: true, results: saveResults, saveData };
      } else {
        // Handle errors
        setTemplatesSaved(saveResults);
        if (saveErrors.length > 0) {
          const errorMsg = saveErrors.length === 1 
            ? `Save failed: ${saveErrors[0]}`
            : `${saveErrors.length} template(s) failed to save.`;
          setSnackbar({
            open: true,
            message: errorMsg,
            severity: 'error'
          });
        }
        return { success: false, results: saveResults, saveData };
      }
    } catch (error) {
      console.error('Save error:', error);
      setSnackbar({
        open: true,
        message: `Error saving templates: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
      return { success: false, results: [], saveData };
    }
  };

  // Execute parallel saves with single consolidated refresh
  const executeOptimizedSaves = async (serverTemplates, userSpaceTemplates) => {
    if(!isDeploying && deployServer) setIsDeploying(true);
    setShowVersionWarning(false);
    
    const savePromises = [];
    
    // Add server save if needed 
    if (hasTemplates(serverTemplates)) {
      savePromises.push(proceedWithSaveOnly(serverTemplates, true));
    }
    
    // Add user space save if needed 
    if (hasTemplates(userSpaceTemplates)) {
      savePromises.push(proceedWithSaveOnly(userSpaceTemplates, false));
    }
    
    // Execute saves in parallel for optimal performance
    if (savePromises.length > 0) {
      try {
        const results = await Promise.allSettled(savePromises);
        
        // Check if all saves were successful
        const allSuccessful = results.every(r => r.status === 'fulfilled' && r.value.success);
        
        if (allSuccessful) {
          // After both saves complete successfully, do single refresh of tree data
          const combinedTemplates = {
            inbound_templates: [...(serverTemplates.inbound_templates || []), ...(userSpaceTemplates.inbound_templates || [])],
            outbound_templates: [...(serverTemplates.outbound_templates || []), ...(userSpaceTemplates.outbound_templates || [])],
            workflow: [...(serverTemplates.workflow || []), ...(userSpaceTemplates.workflow || [])]
          };
          
          // Call handleSaveSuccess once for all saved templates
          handleSaveSuccess(combinedTemplates);
          
          // Fetch updated RHS templates for currently selected templates (for revision IDs and latest data)
          const ibSel = treeData.inbound.leftTree.tree.body.selectedNode;
          const obSel = treeData.outbound.leftTree.tree.body.selectedNode;
          const wfSel = treeData.workflow.leftTree.tree.body.selectedNode;
          
          // Helper function to check if currently selected template was saved
          const isCurrentTemplateSaved = (selectedNode, templateList) => {
            return Object.keys(selectedNode).length && templateList.some(o => 
              `${o.account}/${o.workflow_group}/${o.template}` === `${account}/${selectedNode.workflowGroup}/${selectedNode.title}`
            );
          };
          
          // Helper function to handle recent visited templates logic
          const handleRecentVisitedTemplates = (screen, selectedNode) => {
            ["header", "body", "trailer"].forEach(e => {
              const currentSelectedNode = treeData[screen].rightTree.tree[e].selectedNode;
              if (currentSelectedNode.key){
                let parentKey = currentSelectedNode?.key?.split('-');
                parentKey.pop();
                parentKey = parentKey.join('-');
                const parentNode = getSelectedNodes([parentKey], treeData[screen].rightTree.tree[e].translatedData, false)[0];
                let isSameLevelElseWithEmptyChild = false;
                for (let i = 0; i < parentNode?.children?.length; i++) {
                  if(parentNode?.children[i]?.ICON_DISPLAY === "CONDITION_ELSE" && !parentNode?.children[i]?.children?.length) {
                    isSameLevelElseWithEmptyChild = true;
                    break;
                  }
                }
                if(isSameLevelElseWithEmptyChild) {
                  modifyRecentVisitedTemplates(parentNode, e, screen, account, selectedNode.workflowGroup, selectedNode.title);
                  if (screen === "workflow") {
                    dispatch(treeActions.setRightTreeSelectedNode({screen, type: e, data: parentNode}));
                  }
                }
              }
            });
          };
          
          
          // Handle inbound templates - refresh list, then template details
          if (combinedTemplates.inbound_templates.length > 0) {
            dispatch(treeActions.setRightTreeLoading({data: true, screen: "inbound"}));
            dispatch(api.endpoints.getInboundTemplateList.initiate({account}, {forceRefetch: true})).then(() => {
              // Fetch template details if currently selected template was saved
              if (isCurrentTemplateSaved(ibSel, combinedTemplates.inbound_templates)) {
                dispatch(api.endpoints.getInboundTemplate.initiate({
                  account, 
                  workflowGroupName: ibSel.workflowGroup, 
                  templateName: ibSel.title
                }, { forceRefetch: true })).then(() => {
                  dispatch(treeActions.setRightTreeLoading({data: false, screen: "inbound"}));
                });
                dispatch(
                  treeActions.setLeftTreeContextActionValue({
                    screen: "inbound",
                    data: { name: ibSel.title, workflowGroup: ibSel.workflowGroup },
                  })
                );
              }
            }).finally(() => {
              dispatch(treeActions.setRightTreeLoading({data: false, screen: "inbound"}));
            });

          }
          
          // Handle outbound templates - refresh list, then template details
          if (combinedTemplates.outbound_templates.length > 0) {
            dispatch(treeActions.setRightTreeLoading({data: true, screen: "outbound"}));
            handleRecentVisitedTemplates("outbound", obSel);

            dispatch(api.endpoints.getOutboundTemplateList.initiate({account}, {forceRefetch: true})).then(() => {
              // Fetch template details if currently selected template was saved
              if (isCurrentTemplateSaved(obSel, combinedTemplates.outbound_templates)) {
                dispatch(api.endpoints.getOutboundTemplate.initiate({
                  account, 
                  workflowGroupName: obSel.workflowGroup, 
                  templateName: obSel.title
                }, { forceRefetch: true })).then(() => {
                  dispatch(treeActions.setRightTreeLoading({data: false, screen: "outbound"}));
                });
                dispatch(
                  treeActions.setLeftTreeContextActionValue({
                    screen: "outbound",
                    data: { name: obSel.title, workflowGroup: obSel.workflowGroup },
                  })
                );
              }
            }).finally(() => {
              dispatch(treeActions.setRightTreeLoading({data: false, screen: "outbound"}));
            });
          }
          
          // Handle workflow templates - refresh list, then template details
          if (combinedTemplates.workflow.length > 0) {
            dispatch(treeActions.setRightTreeLoading({data: true, screen: "workflow"}));
            handleRecentVisitedTemplates("workflow", wfSel);
            dispatch(api.endpoints.getWorkflows.initiate({account}, {forceRefetch: true})).then(() => {
              // Fetch template details if currently selected template was saved
              if (isCurrentTemplateSaved(wfSel, combinedTemplates.workflow)) {
                dispatch(api.endpoints.getWorkflowTree.initiate({
                  account, 
                  workflowGroupName: wfSel.workflowGroup, 
                  templateName: wfSel.title
                }, { forceRefetch: true })).then(() => {
                  dispatch(treeActions.setRightTreeLoading({data: false, screen: "workflow"}));
                });
                dispatch(
                  treeActions.setLeftTreeContextActionValue({
                    screen: "workflow",
                    data: { name: wfSel.title, workflowGroup: wfSel.workflowGroup },
                  })
                );
              }
            }).finally(() => {
              dispatch(treeActions.setRightTreeLoading({data: false, screen: "workflow"}));
            });
          }
                  
          // Store save results 
          const allResults = results.flatMap(r => r.status === 'fulfilled' ? r.value.results : []);
          setSaveResults(allResults);
          
          setSaved(true);
        } else {
        }
        
        setIsDeploying(false);
        setSaveLoading(false);
        
      } catch (error) {
        console.error('Error in parallel saves:', error);
        setIsDeploying(false);
        setSaveLoading(false);
      }
    } else {
      // No templates to save
      setIsDeploying(false);
      setSaveLoading(false);
      setSnackbar({
        open: true,
        message: 'No templates selected for deployment.',
        severity: 'info'
      });
    }
  };

  // Handle version conflict dialog responses
  const handleVersionConflictResponse = async(decision) => {
    // Record the decision for current conflict item
    const newDecisions = [...conflictDecisions];
    newDecisions[currentConflictIndex] = {
      item: conflictItems[currentConflictIndex],
      decision: decision
    };
    setConflictDecisions(newDecisions);
    
    if (decision === 'YES') {
      // Move to next conflict or execute optimized saves
      const nextIndex = currentConflictIndex + 1;
      if (nextIndex < conflictItems.length) {
        setCurrentConflictIndex(nextIndex);
        setCurrentConflictItem(conflictItems[nextIndex]);
      } else {
        // All conflicts resolved - execute optimized saves
        const { serverTemplates, userSpaceTemplates } = groupTemplatesBySaveDestination(newDecisions);
        executeOptimizedSaves(serverTemplates, userSpaceTemplates);
      }
    } 
    else if (decision === 'YES_TO_ALL') {
      // All templates go to server - use optimized approach
      setShowVersionWarning(false);
      const serverTemplates = combineTemplatesForDeployment(nonConflictingTemplates, conflictingTemplates, []);
      const userSpaceTemplates = { inbound_templates: [], outbound_templates: [], workflow: [] };
      executeOptimizedSaves(serverTemplates, userSpaceTemplates);
    } 
    else if (decision === 'NO') {
      // Move to next conflict or execute optimized saves
      const nextIndex = currentConflictIndex + 1;
      if (nextIndex < conflictItems.length) {
        setCurrentConflictIndex(nextIndex);
        setCurrentConflictItem(conflictItems[nextIndex]);
      } else {
        // All conflicts processed - execute optimized saves
        const { serverTemplates, userSpaceTemplates } = groupTemplatesBySaveDestination(newDecisions);
        executeOptimizedSaves(serverTemplates, userSpaceTemplates);
      }
    } 
    else if (decision === 'NO_TO_ALL') {
      // Non-conflicting to server, all conflicting to user space
      setShowVersionWarning(false);
      const serverTemplates = nonConflictingTemplates;
      const userSpaceTemplates = conflictingTemplates;
      executeOptimizedSaves(serverTemplates, userSpaceTemplates);
    }
  };

  useEffect(() => {
    if (reloadPopup) {
      setReloadPopup(false);
      window.parent.postMessage(account)
    }
  }, [reloadPopup])

  const setTemplatesSaved = (results = null, showAlertMessage = true) => {
    if (results && results.length > 0) {
      // Update based on actual save results from backend
      setUnsavedList(prev => prev.map(o => {
        const result = results.find(r => {
          // Handle different path formats for different template types
          let constructedPath;
          
          if (r.template_type === 'inbound_templates') {
            constructedPath = r.path.replace('/inbound_templates', '') + '/' + r.name;
          } else if (r.template_type === 'outbound_templates') {
            
            constructedPath = r.path.replace('/outbound_templates', '') + '/' + r.name;
          } else if (r.template_type === 'workflow') {
            constructedPath = `${r.path}/${r.name}`;
          } else {
            // Fallback
            constructedPath = `${r.path}/${r.name}`;
          }
          
          return constructedPath === o.path;
        });
        if (result) {
          return {
            ...o,
            saved: result.status === 'success',
            blocked: result.status === 'blocked',
            failed: result.status === 'failed',
            saveMessage: result.message || ''
          };
        }
        return o;
      }));
    } else {
      // Fallback to original behavior
      setUnsavedList(prev => prev.map(o => {
        return {...o, saved: checkedTemplates.includes(o.path)}
      }));
    }
    
    if (selectedTemplate) {
      const templateInfo = getTemplateInfoByPath(selectedTemplate);
      if (results) {
        const result = results.find(r => {
          let constructedPath;
          if (r.template_type === 'inbound_templates') {
            constructedPath = r.path.replace('/inbound_templates', '') + '/' + r.name;
          } else if (r.template_type === 'outbound_templates') {
            constructedPath = r.path.replace('/outbound_templates', '') + '/' + r.name;
          } else if (r.template_type === 'workflow') {
            constructedPath = `${r.path}/${r.name}`;
          } else {
            constructedPath = `${r.path}/${r.name}`;
          }
          return constructedPath === selectedTemplate;
        });
        if (result) {
          setSelectedTemplateInfo({
            ...templateInfo,
            saved: result.status === 'success',
            blocked: result.status === 'blocked',
            failed: result.status === 'failed',
            saveMessage: result.message || ''
          });
        }
      } else {
        setSelectedTemplateInfo({...templateInfo, saved: true});
      }
    }
     setSaved(true);
  }

  const setTemplatesRevertedByPath = (paths) => {
    setUnsavedList(unsavedList.map(obj => {
      return {...obj, reverted: paths.map(o => o.path).includes(obj.path)};
    }))
  }

  const handleCheckbox = (path) => {
    if (path === 'all') {
      // Select all templates that can be saved (not saved, reverted, blocked, or successfully deployed)
      const savableTemplates = unsavedList.filter(o => !o.saved && !o.reverted && !o.blocked && !o.deploySuccess).map(o => o.path);
      setCheckedTemplates(checkedTemplates.length === savableTemplates.length ? [] : savableTemplates);
    } else if (checkedTemplates.includes(path)) {
      setCheckedTemplates(prev => prev.filter(str => str !== path));
    } else {
      setCheckedTemplates(prev => [...prev, path]);
    }
  }

  //return list of savable templates ordered by template type
  const getSaveableByTemplateType = () => {
    let savableTemplates = unsavedList.filter(o => checkedTemplates.includes(o.path) && !o.saved);
    let inbound_templates = [];
    let outbound_templates = [];
    let workflow = [];

    savableTemplates.forEach(o => {
      if (o.template_type === 'inbound') inbound_templates.push({
        account: o.account,
        workflow_group: o.workflow_group,
        template: o.template
      });
      if (o.template_type === 'outbound') outbound_templates.push({
        account: o.account,
        workflow_group: o.workflow_group,
        template: o.template
      });
      if (o.template_type === 'workflow') workflow.push({
        account: o.account,
        workflow_group: o.workflow_group,
        template: o.template
      });
    })

    return { inbound_templates, outbound_templates, workflow };
  }

  // On save succces, update current templates' initial data to be in sync with what was just saved.
  // This allows us to update the initial data so it can pass the diff check properly, without needing
  // to fetch the data directly to get initial data. This way, the user's right tree state is not altered in this process.
  const handleSaveSuccess = (ordered) => {
    let ibSel = treeData.inbound.leftTree.tree.body.selectedNode;
    let obSel = treeData.outbound.leftTree.tree.body.selectedNode;
    let wfSel = treeData.workflow.leftTree.tree.body.selectedNode;
    
    const setInitialData = (type) => {
      if (type === 'inbound') {
        let ibData = rcTreeToInboundTemplate(treeData.inbound.rightTree.tree)
        dispatch(treeActions.setRightTreeInitialFetchData({
          screen: 'inbound',
          data: ibData
        }));
        dispatch(treeActions.setRightTreeInitialTemplateHeader({screen: type, value:treeData.inbound.rightTree.tree.templateHeader }));

        // dispatch(treeActions.setRightTreeInitialTemplateHeader({
        //   screen: 'inbound',
        //   value: treeData.inbound.rightTree.tree.templateHeader
        // }));
        // ["header", "body", "trailer"].forEach(e => {
        //   dispatch(treeActions.setRightTreeData({screen: type, type: e, data: []}));
        //   dispatch(treeActions.setNewRightTreeTranslatedData({screen: type, type: e, data: []}))
        //   dispatch(treeActions.setRightTreeSelectedNode({screen: type, type: e, data: {}}))
        // })
        // dispatch(treeActions.setRightTreeInitialTemplateHeader({screen: type, value: []}));
        // dispatch(treeActions.setRightTreeTemplateHeader({screen: type, value: []}));
        // dispatch(treeActions.setRightTreeLoading({screen: type, data: true}));
        // dispatch(api.endpoints.getInboundTemplateList.initiate(
        //   {account},
        //   {subscribe:false}
        // ))
        // dispatch(api.endpoints.getInboundTemplate.initiate({
        //   account: account,
        //   workflowGroupName: treeData.inbound.leftTree.tree.body.selectedNode?.workflowGroup,
        //   templateName: treeData.inbound.leftTree.tree.body.selectedNode?.title
        // }, {forceRefetch : true}))

        //if current selected template was checked during save success, set templateChanged to false.
        if (Object.keys(ibSel).length && ordered.inbound_templates.filter(o => `${o.account}/${o.workflow_group}/${o.template}` === `${account}/${ibSel.workflowGroup}/${ibSel.title}`).length) {
          dispatch(treeActions.setTemplateChanged({screen: type, data: false}));
        }
        
      }
      if (type === 'outbound') {
        let obData = treeData.outbound.rightTree.tree.templateHeader.FILETYPE === "XML" ? rcTreeToOutboundXML(treeData.outbound.rightTree.tree, treeData.outbound.rightTree.initialFetchData) : rcTreeToOutboundTemplate(treeData.outbound.rightTree.tree);
        /// Will need outbound rctree reverse translate function for this
        dispatch(treeActions.setRightTreeInitialFetchData({
          screen: 'outbound',
          data: obData
        }));
        dispatch(treeActions.setRightTreeInitialTemplateHeader({screen: type, value:treeData.outbound.rightTree.tree.templateHeader }));

        // ["header", "body", "trailer"].forEach(e => {
        //   dispatch(treeActions.setRightTreeData({screen: type, type: e, data: []}));
        //   dispatch(treeActions.setNewRightTreeTranslatedData({screen: type, type: e, data: []}))
        //   dispatch(treeActions.setRightTreeSelectedNode({screen: type, type: e, data: {}}))
        // });
        // dispatch(treeActions.setRightTreeInitialTemplateHeader({screen: type, value: []}));
        // dispatch(treeActions.setRightTreeTemplateHeader({screen: type, value: []}));
        // dispatch(treeActions.setRightTreeLoading({screen: type, data: true}));
        // dispatch(api.endpoints.getOutboundTemplateList.initiate(
        //   {account},
        //   {subscribe:false}
        // ))
        // dispatch(api.endpoints.getOutboundTemplate.initiate({
        //   account: account,
        //   workflowGroupName: treeData.outbound.leftTree.tree.body.selectedNode?.workflowGroup,
        //   templateName: treeData.outbound.leftTree.tree.body.selectedNode?.title
        // }, {forceRefetch : true}))

        if (Object.keys(obSel).length && ordered.outbound_templates.filter(o => `${o.account}/${o.workflow_group}/${o.template}` === `${account}/${obSel.workflowGroup}/${obSel.title}`).length) {
          dispatch(treeActions.setTemplateChanged({screen: type, data: false}));
        }
      }
      if (type === 'workflow') {
        let wfData = rcTreeToWorkflowTemplate(treeData.workflow.rightTree.tree);
        /// Will need outbound rctree reverse translate function for this
        dispatch(treeActions.setRightTreeInitialFetchData({
          screen: 'workflow',
          data: wfData
        }));

        dispatch(treeActions.setRightTreeInitialTemplateHeader({screen: type, value:treeData.outbound.rightTree.tree.templateHeader }));
        
        //if current selected template was checked during save success, set templateChanged to false.
        if (Object.keys(wfSel).length && ordered.workflow.filter(o => `${o.account}/${o.workflow_group}/${o.template}` === `${account}/${wfSel.workflowGroup}/${wfSel.title}`).length) {
          dispatch(treeActions.setTemplateChanged({screen: type, data: false}));
        }

      }
    }

    //if one of the saved templates is a template you are currently on, update its initial data.
    if (Object.keys(ibSel).length) {
      ordered.inbound_templates.forEach(o => {
        if (`${o.account}/${o.workflow_group}/${o.template}` === `${account}/${ibSel.workflowGroup}/${ibSel.title}`) {
          setInitialData('inbound');
        }
      })
    }
    if (Object.keys(obSel).length) {
      ordered.outbound_templates.forEach(o => {
        if (`${o.account}/${o.workflow_group}/${o.template}` === `${account}/${obSel.workflowGroup}/${obSel.title}`) {
          setInitialData('outbound');
        }
      })
    }
    if (Object.keys(wfSel).length) {
      ordered.workflow.forEach(o => {
        if (`${o.account}/${o.workflow_group}/${o.template}` === `${account}/${wfSel.workflowGroup}/${wfSel.title}`) {
          setInitialData('workflow');
        }
      })
    }

    //refetch unsaved templates to force an update on wfg tree unsaved nodes.
    dispatch(api.endpoints.getUnsavedTemplates.initiate(
      { account }, { forceRefetch: true }
    ));

    setListLoading(false);
  }

  const handleRevertSuccess = (revertedPaths) => {
    const currSelectedTemplate = treeData[treeData.currentScreen].leftTree.tree.body.selectedNode;
    const rightTree = treeData[treeData.currentScreen].rightTree;

    // if (rightTree.path) {
    //   dispatch(treeActions.setRightTreeLoading({ data: true, screen: treeData.currentScreen }));
    //   ["header", "body", "trailer"].forEach((e) => {
    //     dispatch(
    //       treeActions.setRightTreeData({ screen: treeData.currentScreen, type: e, data: [] })
    //     );
    //     dispatch(
    //       treeActions.setRightTreeTransData({ screen: treeData.currentScreen, type: e, data: [] })
    //     );
    //     dispatch(
    //       treeActions.setRightTreeSelectedNode({ screen: treeData.currentScreen, type: e, data: {} })
    //     );
    //   });

    //   if (treeData.currentScreen === "inbound") {
    //     dispatch(api.endpoints.getInboundTemplate.initiate(
    //       {
    //         account, 
    //         workflowGroupName: currSelectedTemplate.workflowGroup, 
    //         templateName: currSelectedTemplate.title,
    //         structonly: false
    //       },
    //       {subscribe: false, forceRefetch: true}
    //     )).then(() => {
    //       dispatch(treeActions.setRightTreeSelectedNode(
    //         {
    //           screen: treeData.currentScreen,
    //           type: 'body',
    //           data: treeData[treeData.currentScreen].rightTree.tree.body.translatedData[0],
    //         }
    //       ))
    //     });
    //   } else if (treeData.currentScreen === "outbound") {
    //     dispatch(api.endpoints.getOutboundTemplate.initiate(
    //       {
    //         account, 
    //         workflowGroupName: currSelectedTemplate.workflowGroup, 
    //         templateName: currSelectedTemplate.title,
    //         structonly: false
    //       },
    //       {subscribe: false, forceRefetch: true}
    //     ));
    //   } else if (treeData.currentScreen === "workflow") {
    //     dispatch(api.endpoints.getWorkflowTree.initiate(
    //       {
    //         account,
    //         workflowGroupName: currSelectedTemplate.workflowGroup,
    //         templateName: currSelectedTemplate.title,
    //       }, { forceRefetch: true }
    //     ))
    //   }
    // }

    // Check if any reverted templates are selected in other screens and refresh them
    revertedPaths.forEach(revertedPath => {
      const { path } = revertedPath;
      
      // Parse path to get template info (format: account/workflowGroup/template)
      const pathParts = path.split('/');
      if (pathParts.length >= 3) {
        const templateAccount = pathParts[0];
        const workflowGroup = pathParts[1];
        const templateName = pathParts[2];
        console.log('Reverted template:', templateAccount, workflowGroup, templateName);

        // Check inbound screen
        const inboundSelected = treeData.inbound.leftTree.tree.body.selectedNode;
        console.log('Inbound selected:', inboundSelected);
        if (Object.keys(inboundSelected).length && 
            inboundSelected.workflowGroup === workflowGroup && 
            inboundSelected.title === templateName &&
            templateAccount === account) {
          
          dispatch(api.endpoints.getInboundTemplate.initiate(
            {
              account: templateAccount, 
              workflowGroupName: workflowGroup, 
              templateName: templateName,
              structonly: false
            },
            {subscribe: false, forceRefetch: true}
          )).then(() => {
            dispatch(treeActions.setRightTreeSelectedNode(
              {
                screen: "inbound",
                type: 'body',
                data: treeData["inbound"]?.rightTree?.tree?.body?.translatedData?.[0]  || {},
              }
            ))
            dispatch(treeActions.setRightTreeCurrentTab({screen: "inbound", data: 'body'}));
          });

        }
        
        // Check outbound screen
        const outboundSelected = treeData.outbound.leftTree.tree.body.selectedNode;
        if (Object.keys(outboundSelected).length && 
            outboundSelected.workflowGroup === workflowGroup && 
            outboundSelected.title === templateName &&
            templateAccount === account) {
          
          dispatch(api.endpoints.getOutboundTemplate.initiate(
            {
              account: templateAccount, 
              workflowGroupName: workflowGroup, 
              templateName: templateName,
              structonly: false
            },
            {subscribe: false, forceRefetch: true}
          )).then(() => {
            dispatch(treeActions.setRightTreeSelectedNode(
              {
                screen: "outbound",
                type: 'body',
                data: treeData["outbound"]?.rightTree?.tree?.body?.translatedData?.[0]  || {},
              }
            ))
            dispatch(treeActions.setRightTreeCurrentTab({screen: "outbound", data: 'body'}));
          });
        }
        
        // Check workflow screen
        const workflowSelected = treeData.workflow.leftTree.tree.body.selectedNode;
        if (Object.keys(workflowSelected).length && 
            workflowSelected.workflowGroup === workflowGroup && 
            workflowSelected.title === templateName &&
            templateAccount === account) {
          
          dispatch(api.endpoints.getWorkflowTree.initiate(
            {
              account: templateAccount,
              workflowGroupName: workflowGroup,
              templateName: templateName,
            }, { forceRefetch: true }
          )).then(() => {
            dispatch(treeActions.setRightTreeSelectedNode(
              {
                screen: "workflow",
                type: 'body',
                data: treeData["workflow"]?.rightTree?.tree?.body?.translatedData?.[0]  || {},
              }
            ))
            dispatch(treeActions.setRightTreeCurrentTab({screen: "workflow", data: 'body'}));
          });
        }
      }
    });
    
    setTemplatesRevertedByPath(revertedPaths);
    setRevertModalOpen(false);

    //after revert success, set unsaved templates list 
    dispatch(treeActions.setUnsavedTemplates({
      inbound_templates: [],
      outbound_templates: [],
      workflow: [],
    }));
  }

  const modifyRecentVisitedTemplates = (node, tab, screen, account, workflowGroup, title) => {
    const recentlyVisitedTemplates = treeData[screen].recentlyVisitedTemplates;
    const templatePath = `${account}/${workflowGroup}/${title}.json`;
    const translatedData = treeData[screen].rightTree.tree[tab].translatedData;
		if (node) {
			const { key } = node;
			let isModified = false;
			let templates = [];

			// Check if the node exists and update its position
			templates = recentlyVisitedTemplates.map(o => {
				if (o.path === templatePath) {
					if (o.key !== key && o.tab === tab) {
						isModified = true;
						return { path: templatePath, key: key, tab: tab };
					}
					return o;
				}
				return o;
			});

			// If the node doesn't exist, add it to the array
			if (!recentlyVisitedTemplates.some(o => o.path === templatePath && o.tab === tab) && key && (key !== translatedData[0]?.key)) {
				templates.push({ path: templatePath, key: key, tab: tab });
				isModified = true;
			}

			// Dispatch the action only if the array has been modified
			if (isModified) {
				dispatch(treeActions.setRecentlyVisitedTemplates({
					screen: screen,
					value: templates
				})
				)
			}
		}
	};

  const handleSaveSelectedItems = async () => {
    
    setSaveLoading(true);
    let ordered = getSaveableByTemplateType();
    
    // When deploying to the server:
    // Check for conflicts first.
    // If conflicts exist, let the user decide whether to save in user space or on the server.
    // If no conflicts, save directly to the server.
    if (deployServer) {
       try {
        const detectedConflicts = await checkMultipleTemplatesVersionConflicts(dispatch, ordered, account, treeData);
        if (detectedConflicts.length > 0) {
          // Separate conflicting from non-conflicting templates
          const { conflicting, nonConflicting } = separateConflictingTemplates(ordered, detectedConflicts);
          
          // Store both template sets for later combining
          setConflictingTemplates(conflicting);
          setNonConflictingTemplates(nonConflicting);
          
          setConflictItems(detectedConflicts);
          setCurrentConflictIndex(0);
          setCurrentConflictItem(detectedConflicts[0]);
          setConflictDecisions([]);
          setItemsToSkip([]);
          setTimeout(() => {
            setShowVersionWarning(true);

          }, 0);
          // setSaveLoading(false);
          // return;
        } else {
          const userSpaceTemplates = { inbound_templates: [], outbound_templates: [], workflow: [] };
          executeOptimizedSaves(ordered, userSpaceTemplates);
        }
      } catch (error) {
        console.error('Error checking version conflicts:', error);
        setIsDeploying(false);
        setSaveLoading(false);
      }
    } else {
      // Not deploying to server
      const serverTemplates = { inbound_templates: [], outbound_templates: [], workflow: [] };
      executeOptimizedSaves(serverTemplates, ordered);
    }
  };


  // Helper function to combine non-conflicting and approved conflicting templates
  const combineTemplatesForDeployment = (nonConflicting, conflicting, itemsToSkip) => {
    // Start with non-conflicting templates
    const combined = {
      inbound_templates: [...(nonConflicting.inbound_templates || [])],
      outbound_templates: [...(nonConflicting.outbound_templates || [])],
      workflow: [...(nonConflicting.workflow || [])]
    };
    
    // Add conflicting templates that are not skipped
    const filteredConflicting = filterTemplatesAfterConflictResolution(conflicting, itemsToSkip);
    
    combined.inbound_templates.push(...(filteredConflicting.inbound_templates || []));
    combined.outbound_templates.push(...(filteredConflicting.outbound_templates || []));
    combined.workflow.push(...(filteredConflicting.workflow || []));
    
    return combined;
  };

  // Helper function to separate conflicting from non-conflicting templates
  const separateConflictingTemplates = (allTemplates, conflictItems) => {
    const conflicting = {
      inbound_templates: [],
      outbound_templates: [],
      workflow: []
    };
    
    const nonConflicting = {
      inbound_templates: [],
      outbound_templates: [],
      workflow: []
    };
    
    // Helper function to check if a template is conflicting
    const isTemplateConflicting = (template, templateType) => {
      return conflictItems.some(conflict => 
        conflict.templateData.template === template.template &&
        conflict.templateData.workflow_group === template.workflow_group &&
        conflict.templateData.template_type === templateType
      );
    };
    
    // Separate inbound templates
    if (allTemplates.inbound_templates) {
      allTemplates.inbound_templates.forEach(template => {
        if (isTemplateConflicting(template, 'inbound')) {
          conflicting.inbound_templates.push(template);
        } else {
          nonConflicting.inbound_templates.push(template);
        }
      });
    }
    
    // Separate outbound templates
    if (allTemplates.outbound_templates) {
      allTemplates.outbound_templates.forEach(template => {
        if (isTemplateConflicting(template, 'outbound')) {
          conflicting.outbound_templates.push(template);
        } else {
          nonConflicting.outbound_templates.push(template);
        }
      });
    }
    
    // Separate workflow templates
    if (allTemplates.workflow) {
      allTemplates.workflow.forEach(template => {
        if (isTemplateConflicting(template, 'workflow')) {
          conflicting.workflow.push(template);
        } else {
          nonConflicting.workflow.push(template);
        }
      });
    }
    
    return { conflicting, nonConflicting };
  };

  const proceedWithSave = async(saveData, shouldSaveToServer = deployServer) => {
    dispatch(api.endpoints.saveAllTemplates.initiate({
      comment,
      save_to_server: shouldSaveToServer,
      data: saveData
    }, { forceRefetch: true }
    )).then(res => {
      
      const saveResults = res.data?.data?.results || [];
      const saveErrors = res.data?.data?.errors || [];
      
      if (saveErrors.length > 0) {
        console.warn('Save Errors:', saveErrors);
      }

      if (res.isSuccess && res.data?.current_state === "SUCCESS") {
        setIsDeploying(false);
        const inboundLeftTreeSelectedNode = treeData.inbound.leftTree.tree.body.selectedNode;
        const outboundLeftTreeSelectedNode = treeData.outbound.leftTree.tree.body.selectedNode;
        const workflowLeftTreeSelectedNode = treeData.workflow.leftTree.tree.body.selectedNode;

        if (saveData?.inbound_templates?.length) {
          dispatch(api.endpoints.getInboundTemplateList.initiate({account},{forceRefetch: true})).then(() => {
            // to get updated revision id need to fetch template on server
            if (deployServer) dispatch(api.endpoints.getInboundTemplate.initiate({account, workflowGroupName: inboundLeftTreeSelectedNode.workflowGroup, templateName: inboundLeftTreeSelectedNode.title},{ forceRefetch: true}))
            dispatch(
              treeActions.setLeftTreeContextActionValue({
                screen: "inbound",
                data: { name: inboundLeftTreeSelectedNode.title, workflowGroup: inboundLeftTreeSelectedNode.workflowGroup },
              })
            );
          });
        }

        if (saveData?.outbound_templates?.length) {
          dispatch(treeActions.setRightTreeLoading({data: true, screen: "outbound"}));
          ["header", "body", "trailer"].forEach(e => {
            const selectedNode = treeData.outbound.rightTree.tree[e].selectedNode;
            if (selectedNode.key){
              let parentKey = selectedNode?.key?.split('-');
              parentKey.pop();
              parentKey =parentKey.join('-');
              const parentNode = getSelectedNodes([parentKey], treeData.outbound.rightTree.tree[e].translatedData, false)[0];
              let isSameLevelElseWithEmptyChild = false;
              for (let i = 0; i < parentNode?.children?.length; i++) {
                if(parentNode?.children[i]?.ICON_DISPLAY === "CONDITION_ELSE" && !parentNode?.children[i]?.children?.length) {
                  isSameLevelElseWithEmptyChild = true;
                  break;
                }
              }
              if(isSameLevelElseWithEmptyChild) {
                modifyRecentVisitedTemplates(parentNode, e, "outbound", account, outboundLeftTreeSelectedNode.workflowGroup, outboundLeftTreeSelectedNode.title)
              }
            }
          })
          dispatch(api.endpoints.getOutboundTemplateList.initiate({account},{forceRefetch: true})).then(() => {  
            dispatch(api.endpoints.getOutboundTemplate.initiate({account, workflowGroupName: outboundLeftTreeSelectedNode.workflowGroup, templateName: outboundLeftTreeSelectedNode.title },{forceRefetch: true}))
            .then(() => {
              dispatch(treeActions.setRightTreeLoading({data: false, screen: "outbound"}))
            })
            dispatch(
              treeActions.setLeftTreeContextActionValue({
                screen: "outbound",
                data: { name: outboundLeftTreeSelectedNode.title, workflowGroup: outboundLeftTreeSelectedNode.workflowGroup },
              })
            );
            
          }).finally(() => {
            dispatch(treeActions.setRightTreeLoading({data: false, screen: "outbound"}))
          })
        }

        if (saveData?.workflow?.length) {
          ["header", "body", "trailer"].forEach(e => {
            const selectedNode = treeData.workflow.rightTree.tree[e].selectedNode;
            if (selectedNode.key){
              let parentKey = selectedNode?.key?.split('-');
              parentKey.pop();
              parentKey =parentKey.join('-');
              const parentNode = getSelectedNodes([parentKey], treeData.workflow.rightTree.tree[e].translatedData, false)[0];
              let isSameLevelElseWithEmptyChild = false;
              for (let i = 0; i < parentNode?.children?.length; i++) {
                if(parentNode?.children[i]?.ICON_DISPLAY === "CONDITION_ELSE" && !parentNode?.children[i]?.children?.length) {
                  isSameLevelElseWithEmptyChild = true;
                  break;
                }
              }
              if(isSameLevelElseWithEmptyChild) {
                modifyRecentVisitedTemplates(parentNode, e, "workflow", account, workflowLeftTreeSelectedNode.workflowGroup, workflowLeftTreeSelectedNode.title)
                dispatch(treeActions.setRightTreeSelectedNode({screen: "workflow", type: e, data: parentNode}))
              }
            }
          })
          dispatch(treeActions.setRightTreeLoading({data: true, screen: "workflow"}))
          dispatch(api.endpoints.getWorkflows.initiate({account},{forceRefetch: true})).then(() => {
            dispatch(api.endpoints.getWorkflowTree.initiate({account, workflowGroupName: workflowLeftTreeSelectedNode.workflowGroup, templateName: workflowLeftTreeSelectedNode.title },{forceRefetch: true}))
            .then(() => {
              dispatch(treeActions.setRightTreeLoading({data: false, screen: "workflow"}))
            })
            dispatch(
              treeActions.setLeftTreeContextActionValue({
                screen: "workflow",
                data: { name: workflowLeftTreeSelectedNode.title, workflowGroup: workflowLeftTreeSelectedNode.workflowGroup },
              })
            );
          }).finally(() => {
            dispatch(treeActions.setRightTreeLoading({data: false, screen: "workflow"}))
          })
        }
          
        handleSaveSuccess(saveData);
        
        // Also update templates with save results even on success
        setTemplatesSaved(saveResults);
        setSaveLoading(false);
      } else if (res.isError || res.data?.current_state === "FAILED") {
        const saveResults = res.data?.data?.results || [];
        const saveErrors = res.data?.data?.errors || [];
        const summary = res.data?.data?.summary || {};
        
        
        console.warn('Save request had errors:', {
          summary,
          errors: saveErrors,
          results: saveResults
        });
        
        // Update templates with their individual save results
        setTemplatesSaved(saveResults);
        // setListLoading(false);
        // setSaveLoading(false);
        
        // Failed templates are added to errors array, blocked templates are not
        if (saveErrors.length > 0) {
          const errorMsg = saveErrors.length === 1 
            ? `Save failed: ${saveErrors[0]}`
            : `${saveErrors.length} template(s) failed to save. Check details for more information.`;
          setSnackbar({
            open: true,
            message: errorMsg,
            severity: 'error'
          });
        }
        
        // Also check for any blocked templates and log them
        const blockedTemplates = saveResults.filter(r => r.status === 'blocked');
        if (blockedTemplates.length > 0) {
          console.warn('Blocked templates:', blockedTemplates);
        }
      } else {
        //case: unexpected response
        console.warn('*Dev message* Save request failure - unexpected: ', res);
        // setListLoading(false);
        // setSaveLoading(false);
      }
    });
  }

  //map through list of unsaved templates and return table rows
  const UnsavedTemplateRows = () => unsavedList.map((o, i) => {
    if (!o?.warnings) {
      o.warnings = [];
      //BG-8842 - add console warn for odd case where warnings are undefined, 
      // causing an error when referencing o.warnings.length
      console.warn(o);
    }
    // Determine status icon, status text, and hover message
    // Priority order: deployFailed > deploySuccess > saved > reverted > blocked > failed > warnings > verified
    let statusIcon, statusText, hoverMessage;
    
    if (o.deployFailed) {
      // Deployment failed - show as error with deployment message
      statusIcon = <ErrorIcon sx={styles.errorIcon}/>;
      statusText = "Deploy Failed";
      hoverMessage = o.deployMessage || 'Deployment failed';
    } else if (o.deploySuccess) {
      // Deployment succeeded - show as success
      statusIcon = <CheckCircleIcon sx={styles.successIcon}/>;
      statusText = "Deployed";
      hoverMessage = o.deployMessage || 'Successfully deployed to server';
    } else if (o.saved) {
      // If template is saved successfully, always show saved status (ignore warnings)
      statusIcon = <CheckCircleIcon sx={styles.successIcon}/>;
      statusText = t('LBL_SAVED');
      hoverMessage = t('LBL_SAVED');
    } else if (o.reverted) {
      statusIcon = <CheckCircleIcon sx={styles.successIcon}/>;
      statusText = t('LBL_REVERTED');
      hoverMessage = t('LBL_REVERTED');
    } else if (o.blocked) {
      statusIcon = <ErrorIcon sx={styles.errorIcon}/>;
      statusText = "Error";
      hoverMessage = o.saveMessage || t('LBL_BLOCKED');
    } else if (o.failed) {
      // Failed templates show as errors
      statusIcon = <ErrorIcon sx={styles.errorIcon}/>;
      statusText = "Error";
      hoverMessage = o.saveMessage || t('LBL_SAVE_FAILED');
    } else if (o.warnings && o.warnings.length > 0 && !o.saved && !o.reverted && !o.blocked) {
      statusIcon = <WarningRoundedIcon sx={styles.warningIcon}/>;
      statusText = "Warning";
      hoverMessage = `${o.warnings.length} ${t('_FRG_WARNINGS_FOUND')}`;
    } else {
      statusIcon = <CheckCircleIcon sx={styles.successIcon}/>;
      statusText = t('LBL_VERIFIED');
      hoverMessage = t('LBL_VERIFIED');
    }

    return <TableRow 
      hover
      key={`unsavedtemplate-${i}`}
      sx={{'&:last-child td, &:last-child th': { border: 0 } }}
      className={'hover-pointer ' + 
        (o.path === selectedTemplate ? 'selected-table-row' : '')
      }
    >
      {/* Checkbox */}
      <TableCell sx={styles.td} onClick={() => handleCheckbox(o.path)}>
        <Checkbox 
          size='small' 
          sx={styles.checkbox} 
          disabled={o.saved || o.reverted || o.blocked || o.deploySuccess}
          checked={!o.saved && !o.reverted && !o.blocked && !o.deploySuccess && checkedTemplates.includes(o.path)}
        />
      </TableCell>

      {/* Template type */}
      <TableCell sx={styles.td} onClick={() => handleRowClick(o.path)}>
        <div className="flex align-center">
          {typeIcons[o.template_type]}
          <span className="button-centered-text">{t(o.template_type === "workflow" ? `${o.template_type.toUpperCase()}_` : o.template_type.toUpperCase())}</span>
        </div>
      </TableCell>

      {/* Account */}
      <TableCell align="left" sx={styles.td} onClick={() => handleRowClick(o.path)}>
        {o.account}
      </TableCell>

      {/* Wf Group */}
      <TableCell align="left" sx={styles.td} onClick={() => handleRowClick(o.path)}>
        {o.workflow_group}
      </TableCell>

      {/* Template name */}
      <TableCell
        align="left"
        sx={styles.td}
        onClick={() => handleRowClick(o.path)}
      >
        {o.template}
      </TableCell>

      {/* Status */}
      <TableCell align="left" sx={styles.td} onClick={() => handleRowClick(o.path)}>
        <LightTooltip title={hoverMessage} placement="top" arrow>
          <div className="flex align-center hover-pointer" style={styles.status}>
            {statusIcon}
            {statusText}
          </div>
        </LightTooltip>
      </TableCell>
    </TableRow>
  });

  const alertUser = (event) => {
    event.preventDefault();
    event.returnValue = ''; 
    return ''
  }

  const handleTabClosing = () => {return false};

  useEffect(() => {
    // on unsavedList change, check for selectedTemplate, and update info. Useful in case user is 
    // reopening the same template that they just saved.
    if (selectedTemplate) setSelectedTemplateInfo(getTemplateInfoByPath(selectedTemplate));
  }, [unsavedList]);

  useEffect(() => {
    //on `selectedTemplate` update, mirror the template's info to `selectedTemplateInfo` state.
    if (selectedTemplate) setSelectedTemplateInfo(getTemplateInfoByPath(selectedTemplate));
  }, [selectedTemplate]);

  useEffect(() => {
    //after save finished, only close dialog if all templates are successfully saved or reverted
    //DO NOT close if there are any errors (failed/blocked templates)
    if (saved) {
      // Check if all templates are successfully saved or reverted (no errors)
      const hasErrors = unsavedList.some(o => o.failed || o.blocked);
      const remainingUnsaved = unsavedList.filter(o => !o.saved && !o.reverted && !o.blocked && !o.failed);
      
      // Generate message based on save results
      const getResultMessage = (results) => {
        if (!results || results.length === 0) {
          return { message: 'Templates saved!', severity: 'success' };
        }
        
        const successful = results.filter(r => r.status === 'success').length;
        const blocked = results.filter(r => r.status === 'blocked').length;
        const failed = results.filter(r => r.status === 'failed').length;
        const total = results.length;
        
        if (successful === total) {
          return { message: 'Templates saved successfully!', severity: 'success' };
        } else if (successful > 0) {
          return { message: `${successful}/${total} templates saved successfully. ${blocked + failed} had issues.`, severity: 'warning' };
        } else {
          return { message: `Save failed. ${blocked} blocked, ${failed} failed.`, severity: 'error' };
        }
      };
      
      const resultMessage = getResultMessage(saveResults);
      
      setSnackbar({
        open: true,
        message: resultMessage.message,
        severity: resultMessage.severity
      });
      
      // Only close dialog if no errors AND no remaining unsaved templates
      if (!hasErrors && remainingUnsaved.length === 0) {
        //if no unsaved templates are left and no errors occurred
        //reset the window listener that triggers when user tries to leave page, just incase.
        //close dialog.
        window.removeEventListener('beforeunload', alertUser);
        window.removeEventListener('unload', handleTabClosing);
        // setAlertOpen(true);
        // do not close modal , otherwise version conflict dialog won't show up
        // handleModalClose();
      }
      // If there are errors (failed/blocked), keep dialog open so user can see them
      setSaved(false);
    }
  }, [saved]);

  return (
    <>
      <Dialog 
        maxWidth="lg" 
        onClose={handleModalClose} 
        open={dialogOpen}
        hidden={warningsModalOpen || revertModalOpen || whatChangedOpen || showVersionWarning}
        className="dialog-top"
      >
        <DialogTitle sx={{padding: "16px 16px 16px 24px"}} className="divider-bottom">
          <div className="flex space-between">
            <div className="dialog-header">
              <div className="title">{t('DIAG_SAVE_TIT')}</div>
              <div className="description">{t('DIAG_SAVE_DESC')}</div>
            </div>
            <IconButton
              onClick={handleModalClose}
              disabled={isDeploying || saveLoading}
              className="dialog-close"
              sx={{
                padding: 0,
                '&.Mui-disabled': {
                  opacity: 0.5
                }
              }}
            >
              <CloseIcon sx={{
                fill: '#666666',
                fontSize: "28px"
              }} />
            </IconButton>
          </div>
        </DialogTitle>
        { listLoading ? (
          <TableLoading />
        ) : unsavedList.length ? (
            <div className="padding-12 height-100 overflow-auto" style={{maxHeight: '450px'}}>
              {/* Unsaved Templates List */}
              <TableContainer sx={{boxShadow: 0}} >
                <Table size="small" sx={{ minWidth: 550 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell className="test" sx={styles.headerCheckbox} align="left">
                        {/* "select all" checkbox*/}
                        <Checkbox
                          size={'small'}
                          checked={checkedTemplates.length === unsavedList.length}
                          onClick={() => handleCheckbox('all')}
                        />
                      </TableCell>
                      <TableCell sx={styles.td} align="left">{t('COL_TYPE')}</TableCell>
                      <TableCell sx={styles.td} align="left">{t('COL_ACC')}</TableCell>
                      <TableCell sx={styles.td} align="left">{t('COL_GROUP')}</TableCell>
                      <TableCell sx={styles.td} align="left">{t('COL_NAME')}</TableCell>
                      <TableCell sx={styles.td} align="left">{t('COL_STATUS')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <UnsavedTemplateRows/>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ) : (
            <div style={{padding: "16px 16px 16px 24px"}} className="grey font-12 width-99">
              {t('NO_UNSAVED_CHANGES')} 
            </div>
          )
        }
          
        {/* Bottom actions for checkboxed items. "Deploy to server", "Revert Selected Items", "Save Selected Items" */}
        <div className="padding-12 divider-top">
          {/* Under table, select dependant actions. "Comment", "Display Selected Warnings", "What Changed?" */}
          <div className="flex margin-bottom-8 width-100">
            <TextField
              label={t('LBL_NEW_COMMENT')}
              multiline
              rows={2}
              value={comment}
              disabled={listLoading || !unsavedList.length}
              className={listLoading || !unsavedList.length ? "op-5" : ""}
              onChange={(e) => setComment(e.target.value)}
              sx={{flexGrow: 1}}
              InputProps={{sx: {height: '100%'}}}
              // inputProps={{sx: {height: '45px'}}}
            />
            <div className="flex-column margin-left-8" style={{gap: "8px"}}>
              <Button
                size="small"
                variant="outlined"
                style={{flexGrow: 1}}
                onClick={handleDisplayWarnings}
                disabled={listLoading || !selectedTemplate || !selectedTemplateInfo || selectedTemplateInfo.saved || selectedTemplateInfo.warnings?.length === 0}
              > 
                <WarningRoundedIcon sx={styles.warningIcon} fontSize="16px"/>
                {t("BTN_VERIFY_WRN")}
              </Button>
              <Button
                size="small"
                style={{flexGrow: 1}}
                variant="outlined"
                onClick={handleWhatChangedOpen}
                disabled={listLoading || !selectedTemplate || !selectedTemplateInfo || selectedTemplateInfo.saved}
              >
                {t("BTN_WHAT")}
              </Button>
            </div>
          </div>
          <div className="flex align-center justify-end width-100">
            <Stack direction={"row"} spacing={"8px"} className="flex align-center">
              {/* add info on hover: */}
              <DeployToServerTooltip
                title={t('DEPLOY_TO_SERVER_DESC')}
                placement="bottom"
              >
                <FormControlLabel
                  disabled={listLoading || !checkedTemplates.length || !unsavedList.filter(o => checkedTemplates.includes(o.path) && !o.saved && !o.reverted && !o.deploySuccess).length}
                  // sx={{height: '20px', marginLeft: '4px'}}
                  className={'width-fit-content'}
                  label={t('DEPLOY_TO_SERVER')}
                  control={
                    <Checkbox 
                      size={'small'}
                      sx={{height: '20px', padding: '5px !important'}}
                      checked={deployServer}
                      onChange={(e, checked) => setDeployServer(checked)}
                    />
                  } 
                />
              </DeployToServerTooltip>
              <Button
                size="small"
                variant="outlined"
                // className="margin-right-8"
                disabled={listLoading || !checkedTemplates.length || !unsavedList.filter(o => checkedTemplates.includes(o.path) && !o.saved && !o.reverted && !o.deploySuccess).length}
                onClick={handleRevertSelectedItems}
              >
                <HistoryIcon fontSize="medium" className="margin-right-4" />
                <span className="button-centered-text">{t('REVERT_CHECKED_ITEMS')}</span>
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={saveLoading || listLoading || !checkedTemplates.length || !unsavedList.filter(o => checkedTemplates.includes(o.path) && !o.saved && !o.reverted && !o.deploySuccess).length}
                onClick={handleSaveSelectedItems}
              >
                {saveLoading ? (
                  <CircularProgress size={16} className="margin-right-4" />
                ) : (
                  <SaveOutlinedIcon fontSize="medium" className="margin-right-4" />
                )}
                <span className="button-centered-text">
                  {t('SAVE_CHECKED_ITEMS')}
                </span>
              </Button>
            </Stack>
          </div>
        </div>
      </Dialog>

      {/* "Revert Selected Items" Dialog */}
      {revertModalOpen ? (
        <SaveRevert 
          revertModalOpen={revertModalOpen}
          setRevertModalOpen={setRevertModalOpen} 
          setUnsavedList={setUnsavedList}
          selectedTemplate={selectedTemplate}
          checkedTemplates={unsavedList.filter(o => checkedTemplates.includes(o.path) && !o.saved)}
          setCheckedTemplates={setCheckedTemplates}
          setTemplatesRevertedByPath={setTemplatesRevertedByPath}
          handleRevertSuccess={handleRevertSuccess}
        />
      ) : ""}

      {/* 'What Changed?' button */}
      {whatChangedOpen ? (
        <SaveDiffViewer 
          whatChangedOpen={whatChangedOpen}
          setWhatChangedOpen={setWhatChangedOpen}
          templateInfo={unsavedList.filter(o => o.path === selectedTemplate)[0]}
        />
      ) : ""}
      
      {/* Display Selected Warnings Dialog */}
      {warningsModalOpen ? (
        <SaveWarning
          warningsModalOpen={warningsModalOpen}
          setWarningsModalOpen={setWarningsModalOpen}
          unsavedList={unsavedList.filter(o => o.warnings?.length > 0)}
          selectedTemplate={selectedTemplate}
          handleModalClose={handleModalClose}
        />
      ) : ""}

      {/* Version Conflict Warning Dialog */}
      <VersionConflictDialog
        open={showVersionWarning}
        // onClose={() => setShowVersionWarning(false)}
        conflictItem={currentConflictItem}
        currentIndex={currentConflictIndex}
        totalConflicts={conflictItems.length}
        onResponse={handleVersionConflictResponse}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', maxWidth: '450px', whiteSpace: 'normal', wordBreak: 'break-word' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

const styles = {
  td: {padding: '0px 12px', height: "14px"},
  warningIcon: {
    fontSize: "20px", 
    margin: "0 4px 4px 0",
    color: yellow[700],
  },
  successIcon: {
    fontSize: "20px", 
    margin: "0 4px 4px 0",
    color: green[700],
  },
  errorIcon: {
    fontSize: "20px", 
    margin: "0 4px 4px 0",
    color: red[700],
  },
  blockedIcon: {
    fontSize: "20px", 
    margin: "0 4px 4px 0",
    color: '#666666',
  },
  headerCheckbox: {padding: '0px 8px', height: "14px"},
  checkbox: {padding: "6px !important", height: "26px !important"},
  status: {color: '#666666'},
  icon: {fill: '#666666', marginRight: '4px', fontSize: "19px"}
}

const typeIcons = {
  inbound: <InputIcon sx={styles.icon}/>,
  outbound: <OutputIcon sx={styles.icon}/>,
  workflow: <CategoryIcon sx={styles.icon}/>,
}

const DeployToServerTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }}/>
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 12,
      fontWeight: 500,
      padding: '8px',
      borderRadius: '4px', //option for bordered tooltip, incase white-on-white is bad UX.
      border: '1px #666666 solid'
    },
}));